import { Label } from '@heidi/design-system/label';
import { toast } from '@heidi/design-system/toast';
import { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { sharedAgentMessages } from '../../shared.messages';

import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';

export type NangoConnectorApprovalResolve = (
  approvalId: string,
  verdict: 'approve' | 'deny',
  reason?: string,
  modifiedInput?: Record<string, unknown> | null
) => Promise<void>;

export interface NangoConnectorApprovalCardProps {
  approval: ApprovalRequestInput;
  onResolve: NangoConnectorApprovalResolve;
}

type PendingVerdict = 'approve' | 'deny' | null;

export function useNangoApprovalSubmit(
  approvalId: string,
  onResolve: NangoConnectorApprovalResolve
): {
  pendingVerdict: PendingVerdict;
  isBusy: boolean;
  submit: (
    verdict: 'approve' | 'deny',
    modifiedInput?: Record<string, unknown> | null
  ) => void;
} {
  const intl = useIntl();
  const [pendingVerdict, setPendingVerdict] = useState<PendingVerdict>(null);

  const submit = (
    verdict: 'approve' | 'deny',
    modifiedInput?: Record<string, unknown> | null
  ) => {
    if (pendingVerdict !== null) return;
    setPendingVerdict(verdict);
    void onResolve(approvalId, verdict, undefined, modifiedInput ?? null).catch(
      (error: unknown) => {
        setPendingVerdict(null);
        toast({
          title: intl.formatMessage(sharedAgentMessages.couldntResolve),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage({
                  defaultMessage: 'Unknown error',
                  id: 'qDwvZ4p3Io',
                }),
          tone: 'destructive',
        });
      }
    );
  };

  return { pendingVerdict, isBusy: pendingVerdict !== null, submit };
}

export function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
